// src/pages/Cart.js

import React, { useContext, useEffect, useState } from 'react';
import { FaTrash, FaPlus, FaMinus } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './Cart.css';
import Footer from '../components/Footer';
import Header from '../components/Header';
import SEO from '../components/SEO';
import { CartContext } from '../context/CartContext';

const METAL_WALLET_VARIANT = (platform) => ({
  id: platform === "Apple" ? "100-apple" : "100-android",
  name: `Metal Wallet (${platform})`,
  description:
    "Machined from solid metal. Holds cards, cash, ID, keys, and an in-built tracking card.",
  price: 69.99,
  link: "/metal-wallet",
  images: ["/models/open.glb"],
  platform,
  quantity: 1,
});

const Cart = () => {
  const { cart, addToCart, updateQuantity, removeFromCart } = useContext(CartContext);
  // Google Merchant Center checkout links land here as /cart?add={feed id}.
  const [pendingWallet, setPendingWallet] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkingOut, setCheckingOut] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const add = (params.get("add") || "").toUpperCase();
    if (!add) return;
    if (add === "METAL-WALLET") {
      // The wallet ships with an Apple or Android tracking card, so ask
      // before adding rather than guessing the platform.
      setPendingWallet(true);
    }
    window.history.replaceState({}, "", "/cart");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addPendingWallet = (platform) => {
    addToCart(METAL_WALLET_VARIANT(platform));
    setPendingWallet(false);
    if (typeof window.gtag === "function") {
      window.gtag("event", "add_to_cart", {
        currency: "USD",
        value: 69.99,
        items: [
          {
            item_id: `metal-wallet-${platform.toLowerCase()}`,
            item_name: `Metal Wallet (${platform})`,
            price: 69.99,
            quantity: 1,
          },
        ],
      });
    }
  };

  const handleIncrement = (id) => {
    const item = cart.find((item) => item.id === id);
    if (item) {
      updateQuantity(id, item.quantity + 1);
    }
  };

  const handleDecrement = (id) => {
    const item = cart.find((item) => item.id === id);
    if (item && item.quantity > 1) {
      updateQuantity(id, item.quantity - 1);
    }
  };

  const getSubtotal = () => {
    return cart.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getGrandTotal = () => {
    return getSubtotal().toFixed(2);
  };

  const handleCheckout = async () => {
    setCheckoutError('');
    setCheckingOut(true);

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'begin_checkout', {
        currency: 'USD',
        value: Number(getGrandTotal()),
      });
    }

    try {
      // Only ids and quantities go up. The prices are set server side so the
      // cart cannot be edited into a cheaper order.
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map((item) => ({ id: item.id, quantity: item.quantity })),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.url) {
        throw new Error(data.error || 'Checkout failed');
      }

      window.location.href = data.url;
    } catch (error) {
      console.error('Could not start checkout:', error);
      setCheckingOut(false);
      setCheckoutError(
        'We could not start checkout. Please try again, or email asray@the-brik.com and we will take your order directly.'
      );
    }
  };

  return (
    <div>
      <SEO
        title="Your Cart"
        description="Review your Brik order and check out securely."
        path="/cart"
        robots="noindex, nofollow"
        preloadModels={["/models/open.glb"]}
      />
      <Header />
      <div className="cart-container">
        <h1 className="cart-title">Your Cart</h1>

        {pendingWallet && (
          <div
            style={{
              background: "#fff4e5",
              border: "2px solid #ff7d1a",
              borderRadius: "12px",
              padding: "18px 20px",
              margin: "0 auto 18px",
              maxWidth: "720px",
              textAlign: "center",
              fontFamily: "'Futura', sans-serif",
            }}
          >
            <p style={{ margin: "0 0 4px", fontWeight: 700, color: "#7a3b00", fontSize: "16px" }}>
              Almost there: which phone does the Metal Brik's owner use?
            </p>
            <p style={{ margin: "0 0 12px", color: "#7a3b00", fontSize: "13px" }}>
              So it ships with the right tracking card.
            </p>
            <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
              {["Apple", "Android"].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => addPendingWallet(p)}
                  style={{
                    padding: "12px 26px",
                    borderRadius: "8px",
                    border: "2px solid #ff7d1a",
                    background: "white",
                    color: "#ff7d1a",
                    fontWeight: 700,
                    fontSize: "15px",
                    cursor: "pointer",
                    fontFamily: "'Futura', sans-serif",
                  }}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Shipping is free on every order. */}
        {cart.length > 0 && (
          <div className="free-shipping-banner">
            <p>
              <strong>Free shipping</strong> on every order.
            </p>
          </div>
        )}

        {cart.length === 0 ? (
          <p className="empty-cart">Your cart is empty.</p>
        ) : (
          <>
            <div className="cart-items">
              {cart.map((item) => (
                <div key={item.id} className="cart-item">
                  <Link to={item.link} className="cart-image-link">
                    {(() => {
                      const src = item.images ? item.images[0] : item.image;
                      const isGlb = typeof src === "string" && src.toLowerCase().endsWith(".glb");
                      if (isGlb) {
                        return (
                          <div
                            className="cart-image"
                            style={{
                              background: "#dcdcdc",
                              overflow: "hidden",
                              display: "block",
                              width: "225px",
                              height: "150px",
                              borderRadius: "5px",
                            }}
                          >
                            <model-viewer
                              src={src}
                              alt={item.name}
                              auto-rotate=""
                              orientation="00deg 00deg 0deg"
                              camera-orbit="0deg 00deg 90%"
                              interaction-prompt="none"
                              disable-zoom=""
                              shadow-intensity="0.8"
                              exposure="1"
                              style={{ width: "100%", height: "100%", background: "#dcdcdc", display: "block", pointerEvents: "none" }}
                            />
                          </div>
                        );
                      }
                      return (
                        <img
                          loading="lazy"
                          src={src}
                          alt={item.name}
                          className="cart-image"
                        />
                      );
                    })()}
                  </Link>
                  <div className="cart-item-details">
                    <Link to={item.link} className="cart-item-name">
                      <h3>{item.name}</h3>
                    </Link>
                    <p>${item.price.toFixed(2)}</p>
                    <div className="quantity-controls">
                      <button className="quantity-btn" onClick={() => handleDecrement(item.id)}>
                        <FaMinus />
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button className="quantity-btn" onClick={() => handleIncrement(item.id)}>
                        <FaPlus />
                      </button>
                    </div>
                  </div>
                  <button className="remove-btn" onClick={() => removeFromCart(item.id)}>
                    <FaTrash />
                  </button>
                </div>
              ))}
            </div>

            <div className="checkout-section">
              <div className="cart-summary">
                <p>Subtotal: ${getSubtotal().toFixed(2)}</p>
                <p>Shipping: Free</p>
                <h2>Total: ${getGrandTotal()}</h2>
                <button
                  className="checkout-btn"
                  onClick={handleCheckout}
                  disabled={checkingOut}
                >
                  {checkingOut ? 'Taking you to checkout...' : 'Proceed to Checkout'}
                </button>
                {checkoutError && (
                  <p style={{ color: "#b71c1c", fontSize: "13px", marginTop: "10px" }}>
                    {checkoutError}
                  </p>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Cart;
